import { useState, useEffect } from "react";
import { Phone, PhoneCall, Volume2, Sparkles } from "lucide-react";
export const CallPreview = () => {
  const [isRinging, setIsRinging] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  useEffect(() => {
    // Animation sequence: ring -> connect -> show conversation
    const ringTimeout = setTimeout(() => {
      setIsRinging(true);
    }, 1000);
    const connectTimeout = setTimeout(() => {
      setIsRinging(false);
      setIsConnected(true);
    }, 3500);
    return () => {
      clearTimeout(ringTimeout);
      clearTimeout(connectTimeout);
    };
  }, []);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isConnected) {
      interval = setInterval(() => {
        setCallDuration(prev => {
          if (prev >= 15) {
            // Reset the animation
            setIsConnected(false);
            setIsRinging(false);
            setCallDuration(0);
            setTimeout(() => setIsRinging(true), 1000);
            setTimeout(() => {
              setIsRinging(false);
              setIsConnected(true);
            }, 3500);
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isConnected]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };
  return <div className="w-full max-w-xs sm:max-w-sm mx-auto">
      <div className="card-premium rounded-2xl sm:rounded-3xl overflow-hidden border-shimmer">
        {/* Call Header */}
        <div className="relative bg-gradient-red text-primary-foreground px-4 sm:px-6 py-6 sm:py-8 flex flex-col items-center gap-3 sm:gap-4 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,hsl(0_0%_100%/0.1),transparent_50%)]" />
          <Sparkles className="absolute top-4 right-4 w-5 h-5 text-gold/50 animate-sparkle" />
          <Sparkles className="absolute bottom-6 left-6 w-4 h-4 text-gold/30 animate-sparkle" style={{
          animationDelay: "1s"
        }} />
          
          <div className={`relative w-18 h-18 sm:w-24 sm:h-24 rounded-full bg-primary-foreground/15 backdrop-blur-sm flex items-center justify-center text-4xl sm:text-5xl transition-all duration-500 ${isRinging ? "animate-gentle-bounce" : isConnected ? "scale-105" : ""}`}>
            🎅
            {isConnected && <div className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-accent flex items-center justify-center">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-accent-foreground animate-pulse" />
              </div>}
          </div>
          <div className="relative text-center">
            <p className="font-serif font-bold text-xl sm:text-2xl tracking-tight">Santa Claus</p>
            <p className="text-xs sm:text-sm text-primary-foreground/70 mt-0.5 sm:mt-1">North Pole Hotline</p>
          </div>
        </div>

        {/* Call Status */}
        <div className="p-4 sm:p-6 h-[160px] sm:h-[180px] bg-gradient-to-b from-card/20 via-card/15 to-transparent backdrop-blur-md flex flex-col items-center justify-center my-2 sm:my-[10px]">
          {!isRinging && !isConnected && <div className="text-center animate-fade-in">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Phone className="w-6 h-6 sm:w-8 sm:h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground font-medium text-sm sm:text-base">Connecting to the North Pole...</p>
            </div>}

          {isRinging && <div className="text-center animate-fade-in">
              <div className="relative">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
                  <PhoneCall className="w-8 h-8 sm:w-10 sm:h-10 text-accent" />
                </div>
                <div className="absolute inset-0 w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-full bg-accent/20 animate-ping" />
              </div>
              <p className="text-foreground font-semibold text-base sm:text-lg mt-3 sm:mt-4">Ringing...</p>
              <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">Santa is picking up! 🔔</p>
            </div>}

          {isConnected && <div className="text-center animate-fade-in space-y-3 sm:space-y-4 w-full">
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full bg-accent/10">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-accent animate-pulse" />
                <span className="text-accent font-semibold text-xs sm:text-sm">Connected</span>
              </div>
              
              <p className="text-3xl sm:text-4xl font-mono text-foreground font-bold tracking-wider">{formatTime(callDuration)}</p>
              
              {/* Sound wave animation */}
              <div className="flex items-center justify-center gap-0.5 sm:gap-1 h-6 sm:h-8">
                {[...Array(7)].map((_, i) => <div key={i} className="w-0.5 sm:w-1 bg-gradient-to-t from-primary/60 to-primary rounded-full" style={{
              height: `${Math.sin((callDuration * 2 + i) * 0.5) * 10 + 12}px`,
              transition: "height 0.2s ease"
            }} />)}
              </div>

              <div className="flex items-center justify-center gap-1.5 sm:gap-2 text-muted-foreground bg-muted/30 rounded-lg sm:rounded-xl px-3 sm:px-4 py-1.5 sm:py-2">
                <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <p className="text-xs sm:text-sm italic">
                  {callDuration < 3 && '"Ho ho ho! Hello there!"'}
                  {callDuration >= 3 && callDuration < 7 && '"What would you like for Christmas?"'}
                  {callDuration >= 7 && callDuration < 11 && '"The elves are working hard!"'}
                  {callDuration >= 11 && '"You\'re on the nice list!" 🎁'}
                </p>
              </div>
            </div>}
        </div>

        {/* Call Controls */}
        <div className="p-4 sm:p-5 bg-secondary/15 backdrop-blur-md flex justify-center">
          <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-elevated transition-all duration-300 ${isConnected ? "bg-destructive hover:bg-destructive/90" : "bg-accent hover:bg-accent/90"} text-white`}>
            <Phone className={`w-5 h-5 sm:w-7 sm:h-7 transition-transform duration-300 ${isConnected ? "rotate-[135deg]" : ""}`} />
          </div>
        </div>
      </div>
    </div>;
};